import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { cloudTerminalService, type CloudTerminalModelConfig, type ModelEntry } from '../../services/cloud-terminal-service.js';

@customElement('cp-cloud-terminal-model-picker')
export class CpCloudTerminalModelPicker extends LitElement {
  createRenderRoot() { return this; }

  @state() private _grouped: Map<string, ModelEntry[]> = new Map();
  @state() private _lastUsed: CloudTerminalModelConfig | null = null;
  @state() private _loading = true;
  @state() private _error = '';

  override connectedCallback(): void {
    super.connectedCallback();
    this._load();
  }

  private async _load(): Promise<void> {
    try {
      const models = await cloudTerminalService.fetchAvailableModels();
      const lastUsed = cloudTerminalService.getLastUsedModel();

      const grouped = new Map<string, ModelEntry[]>();
      for (const m of models) {
        const bucket = grouped.get(m.provider);
        if (bucket) bucket.push(m);
        else grouped.set(m.provider, [m]);
      }

      this._grouped = grouped;
      this._lastUsed = lastUsed;
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Modelle konnten nicht geladen werden';
    } finally {
      this._loading = false;
    }
  }

  private _select(entry: ModelEntry): void {
    const config: CloudTerminalModelConfig = {
      provider: entry.provider,
      model: entry.id,
      displayName: entry.modelDisplayName,
    };
    this.dispatchEvent(new CustomEvent<CloudTerminalModelConfig>('model-selected', {
      detail: config,
      bubbles: true, composed: true,
    }));
  }

  private _isLastUsed(entry: ModelEntry): boolean {
    return this._lastUsed?.model === entry.id && this._lastUsed?.provider === entry.provider;
  }

  render() {
    return html`
      <div class="cp-model-picker">
        <div class="cp-model-picker__header">Modell waehlen</div>
        <div class="cp-model-picker__list">
          ${this._loading
            ? html`<div class="cp-model-picker__loading">Lade Modelle...</div>`
            : this._error
              ? html`<div class="cp-model-picker__loading">${this._error}</div>`
              : this._grouped.size === 0
              ? html`<div class="cp-model-picker__loading">Keine Modelle verfuegbar</div>`
              : Array.from(this._grouped.entries()).map(([, models]) => html`
                  <div class="cp-model-picker__section-header">${models[0].providerDisplayName}</div>
                  ${models.map(m => html`
                    <button
                      class="cp-model-picker__item${this._isLastUsed(m) ? ' cp-model-picker__item--last-used' : ''}"
                      @click=${() => this._select(m)}
                    >
                      <span class="cp-model-picker__model-name">${m.modelDisplayName}</span>
                      ${this._isLastUsed(m)
                        ? html`<span class="cp-model-picker__badge">Zuletzt</span>`
                        : nothing}
                    </button>
                  `)}
                `)
          }
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-cloud-terminal-model-picker': CpCloudTerminalModelPicker;
  }
}
